<template>
	<v-card :flat="flat">
		<assets-list-header
			@updateCategories="handleUpdateCategories"
			@updateLocations="handleUpdateLocations"
			@updateSearch="handleUpdateSearch"
			@updateStatuses="handleUpdateStasuses"
			@onAddNew="onAddClick"
			@onExport="downloadCsv"
			:assetCategories="assetCategories"
			:assetLocations="assetLocations"
			:assetStatuses="assetStatuses"
			:isDownloading="isDownloading"
			:hide-actions="hideActions"
		/>

		<v-data-table v-model="selectedModel" disable-initial-sort :headers="headers" :items="filteredAssets" :pagination.sync="pagination" :rows-per-page-items="rowsPerPageItems" :search="search" :select-all="selectAll">
			<template slot="items" slot-scope="props">
				<tr>
					<td v-if="selectAll">
						<v-checkbox :input-value="props.selected" @change="toggleSelection(props.item)" primary hide-details></v-checkbox>
					</td>
					<td class="text-xs-left" :style="{ 'min-width': '150px' }">
						<router-link :to="{ name: 'asset', params: { id: props.item.id } }" :target="openDetailInNewTab ? '_blank' : ''">{{ props.item.name }}<v-icon v-if="openDetailInNewTab" small class="ml-1">mdi-open-in-new</v-icon></router-link>

						<v-tooltip bottom v-if="assetIsOverdueService(props.item)">
							<template v-slot:activator="{ on }">
								<v-icon small v-on="on" color="warning" class="ml-1">report</v-icon>
							</template>
							<span>Overdue for service</span>
						</v-tooltip>
					</td>
					<td class="text-xs-left">
						<v-chip small label>{{ props.item.assetNo }}</v-chip>
					</td>
					<td class="text-xs-left">{{ props.item.categoryName }}</td>
					<td class="text-xs-left">{{ props.item.locationName }}</td>
					<td class="text-xs-left" v-if="!hideAssigned && !!props.item.assignedTo">
						<router-link v-if="$_hasAccessToEmployee(loggedInEmployee, props.item.assignedTo.id )" :to="{ name: 'employee', params: { id: props.item.assignedTo.id } }" :target="openDetailInNewTab ? '_blank' : ''"
							>{{ props.item.assignedTo.firstName }} {{ props.item.assignedTo.lastName }}</router-link
						>
						<span v-else>
							{{ props.item.assignedTo.firstName }} {{ props.item.assignedTo.lastName }}
						</span>
					</td>
					<td class="text-xs-left">
						<v-chip small :color="getStatusChipColor(props.item.status)" dark>{{ props.item.status }}</v-chip>
					</td>
					<td class="text-xs-left">
						<AppDate :date="props.item.lastUpdatedDateUTC" />
					</td>
					<td class="text-xs-left" v-if="!hideActions">
						<span v-if="props.item.status === 'Retired'"></span>
						<a v-else-if="props.item.assignedTo == null" href="#" @click.stop="onAssignClick(props.item)">Assign to {{ $term('employee', { case: 'title' }) }}</a>
						<a v-else href="#" @click.stop="onUnassignClick(props.item)">Unassign</a>
					</td>
					<td class="text-xs-left" v-if="hideActions && shouldDisplayUnassignBtn">
						<a href="#" @click.stop="onUnassignClick(props.item)">Unassign</a>
					</td>
					<td v-if="!hideActions" class="justify-center px-0">
						<v-menu offset-y>
							<template v-slot:activator="{ on }">
								<v-btn small icon flat v-on="on">
									<v-icon>mdi-dots-horizontal</v-icon>
								</v-btn>
							</template>
							<v-list>
								<v-list-tile v-for="(action, index) in getAvailableActionsForAsset(props.item)" :key="index" @click="action.callback">
									<v-list-tile-action
										><v-icon>{{ action.icon }}</v-icon>
									</v-list-tile-action>
									<v-list-tile-title>{{ action.title }}</v-list-tile-title>
								</v-list-tile>
							</v-list>
						</v-menu>
					</td>
				</tr>
			</template>
		</v-data-table>

		<assign-asset-dialog :dialog="showAssignAssetDialog" :asset="assigningAsset" :is-saving="isAssigning" @closeDialog="showAssignAssetDialog = false" @onSave="handleAssignSave" />

		<confirmation-dialog :dialog="showConfirmDeleteDialog" title="Confirm Delete" text="Are you sure you want to delete this asset?" :isConfirming="isDeleting" @onConfirm="confirmDelete" @onCancel="showConfirmDeleteDialog = false" />

		<confirmation-dialog :dialog="showDuplicateDialog" title="Confirm Duplicate" text="Are you sure you want to duplicate this asset?" :isConfirming="isSaving" @onConfirm="confirmDuplicate" @onCancel="showDuplicateDialog = false" />

		<confirmation-dialog
			v-if="showUnassignDialog"
			:dialog="showUnassignDialog"
			title="Confirm Unassign"
			:text="`Are you sure you want to unassign this asset from ${this.unassigningAsset.assignedTo.firstName} ${this.unassigningAsset.assignedTo.lastName}?`"
			:isConfirming="isSaving"
			has-note-field
			@onConfirm="handleUnassign"
			@onCancel="showUnassignDialog = false"
		/>

		<confirmation-dialog
			v-if="showRetireDialog"
			:dialog="showRetireDialog"
			title="Confirm Retire"
			text="Are you sure you want to retire this asset?"
			:isConfirming="isSaving"
			has-note-field
			@onConfirm="confirmRetire"
			@onCancel="showRetireDialog = false"
		/>

		<confirmation-dialog
			v-if="showActivateDialog"
			:dialog="showActivateDialog"
			title="Confirm Un-retire"
			text="Are you sure you want to un-retire this asset?"
			:isConfirming="isSaving"
			has-note-field
			@onConfirm="confirmActivate"
			@onCancel="showActivateDialog = false"
		/>

		<asset-editor-dialog :dialog="showAssetEditorDialog" :asset="editingAsset" @closeDialog="showAssetEditorDialog = false" />
	</v-card>
</template>

<script>
	import { mapActions, mapGetters } from 'vuex';
	import AssetsListHeader from '@/components/assets/AssetsListHeader';
	import AssetEditorDialog from '@/components/assets/AssetEditorDialog';
	import AssignAssetDialog from '@/components/assets/AssignAssetDialog.vue';
	import moment from 'moment';
	import toast from '@/services/toasts/index.js';

	export default {
		components: {
			AssetsListHeader,
			AssetEditorDialog,
			AssignAssetDialog
		},
		model: {
			event: 'input',
			prop: 'selected'
		},
		props: {
			selected: {
				type: Array,
				default: () => []
			},
			selectAll: {
				type: Boolean,
				default: false
			},
			hideAssigned: {
				type: Boolean,
				default: false
			},
			hideActions: {
				type: Boolean,
				default: false
			},
			displayUnassignBtn: { // Overrides hideActions
				type: Boolean,
				default: false
			},
			openDetailInNewTab: {
				type: Boolean,
				default: false
			},
			employeeId: {
				type: String,
				default: null
			},
			flat: {
				type: Boolean,
				default: false
			}
		},
		data() {
			return {
				showActivateDialog: false,
				activatingId: null,
				showRetireDialog: false,
				retiringId: null,
				showDuplicateDialog: false,
				duplicatingId: null,
				showConfirmDeleteDialog: false,
				deletingId: null,
				pagination: {
					sortBy: 'lastUpdatedDateUTC',
					descending: true,
					rowsPerPage: 12
				},
				rowsPerPageItems: [12, 30, 60, { text: '$vuetify.dataIterator.rowsPerPageAll', value: -1 }],
				search: '',
				showAssignAssetDialog: false,
				assigningAsset: undefined,
				showUnassignDialog: false,
				unassigningAsset: undefined,
				filterCategories: [],
				filterLocations: [],
				filterStatuses: [],
				showAssetEditorDialog: false,
				editingAsset: undefined
			};
		},
		computed: {
			...mapGetters({
				hasLoadedAssets: 'assets/hasLoaded',
				hasLoadedAssetConfiguration: 'assetConfiguration/hasLoaded',
				assets: 'assets/assets',
				isDeleting: 'assets/isDeleting',
				isAssigning: 'assets/isAssigning',
				isSaving: 'assets/isSaving',
				assetCategories: 'assetConfiguration/categories',
				assetLocations: 'assetConfiguration/locations',
				isDownloading: 'assets/isDownloading',
				loggedInEmployee: 'loggedInEmployee'
			}),
			selectedModel: {
				get() {
					return this.selected;
				},
				set(val) {
					this.$emit('input', val);
				}
			},
			headers() {
				const headers = [
					{ text: 'Name', value: 'name' },
					{ text: 'Asset No', value: 'assetNo' },
					{ text: 'Category', value: 'categoryName' },
					{ text: 'Location', value: 'location' },
					{ text: 'Assigned To', value: 'fullName' },
					{ text: 'Status', value: 'status' },
					{ text: 'Last Updated', value: 'lastUpdatedDateUTC' },
					{ text: '', value: 'defaultAction', sortable: false },
					{ text: '', value: 'actions', sortable: false }
				];

				return headers.filter(header => {
					if (this.hideAssigned && header.text === 'Assigned To') return false;
					if (this.hideActions && (header.value === 'actions' || header.value === 'defaultAction')) return false;
					return true;
				});
			},
			filteredAssets() {
				let filteredAssets = [...this.assets];

				if (this.hideAssigned) filteredAssets = filteredAssets.filter(asset => asset.status !== 'Assigned');

				if (this.hideAssigned) filteredAssets = filteredAssets.filter(asset => asset.status !== 'Retired');

				if (this.employeeId) filteredAssets = filteredAssets.filter(asset => asset.assignedTo && asset.assignedTo.id === this.employeeId);

				// filter categories
				if (this.filterCategories.length) {
					const categoryIds = this.assetCategories.filter(x => this.filterCategories.includes(x.name)).map(x => x.id);
					filteredAssets = filteredAssets.filter(asset => categoryIds.includes(asset.categoryId));
				}

				if (this.filterLocations.length) {
					const locationIds = this.assetLocations.filter(x => this.filterLocations.includes(x.name)).map(x => x.id);
					filteredAssets = filteredAssets.filter(asset => locationIds.includes(asset.currentLocationId));
				}

				if (this.filterStatuses.length) {
					filteredAssets = filteredAssets.filter(asset => this.filterStatuses.includes(asset.status));
				}

				// Add table display names that enable search to work correctly
				filteredAssets = filteredAssets.map(x => {
					const category = this.assetCategories.find(y => y.id === x.categoryId);
					const location = this.assetLocations.find(y => y.id === x.currentLocationId);

					return {
						...x,
						fullName: x.assignedTo ? `${x.assignedTo.firstName} ${x.assignedTo.lastName}` : null,
						categoryName: category ? category.name : null,
						locationName: location ? location.name : null
					};
				});

				return filteredAssets;
			},
			assetStatuses() {
				const uniqueStatuses = [...new Set(this.assets.map(asset => asset.status))];
				return uniqueStatuses.map(status => ({ name: status }));
			},
			shouldDisplayUnassignBtn() {
				return this.loggedInEmployee.isAdmin && this.displayUnassignBtn;
			}
		},
		methods: {
			...mapActions({
				assignAsset: 'assets/assign',
				unassignAsset: 'assets/unassign',
				duplicateAsset: 'assets/duplicate',
				downloadCsv: 'assets/downloadCsvList',
				retireAsset: 'assets/retire',
				activateAsset: 'assets/activate'
			}),
			onDeleteClick(id) {
				this.showConfirmDeleteDialog = true;
				this.deletingId = id;
			},
			onDuplicateClick(id) {
				this.showDuplicateDialog = true;
				this.duplicatingId = id;
			},
			onAssignClick(asset) {
				this.assigningAsset = asset;
				this.showAssignAssetDialog = true;
			},
			onUnassignClick(asset) {
				this.unassigningAsset = asset;
				this.showUnassignDialog = true;
			},
			onRetireClick(id) {
				this.showRetireDialog = true;
				this.retiringId = id;
			},
			onActivateClick(id) {
				this.showActivateDialog = true;
				this.activatingId = id;
			},
			async confirmDelete() {
				const result = await this.$store.dispatch('assets/delete', this.deletingId);
				if (result.isSuccess) {
					this.deletingId = null;
					this.showConfirmDeleteDialog = false;
				}
			},
			async confirmDuplicate() {
				const result = await this.duplicateAsset(this.duplicatingId);
				if (result.isSuccess) {
					this.duplicatingId = null;
					this.showDuplicateDialog = false;
					this.$router.push({ name: 'asset', params: { id: result.data.id } });
				}
			},
			async confirmRetire(note) {
				const result = await this.retireAsset({ assetIds: [this.retiringId], notes: note });
				if (result.isSuccess) {
					this.retiringId = null;
					this.showRetireDialog = false;
				}
			},
			async confirmActivate(note) {
				const result = await this.activateAsset({ assetIds: [this.activatingId], notes: note });
				if (result.isSuccess) {
					this.activatingId = null;
					this.showActivateDialog = false;
				}
			},
			getStatusChipColor(status) {
				switch (status) {
					case 'Available':
						return 'success';
					case 'Assigned':
						return 'indigo';
					case 'Retired':
						return 'warning';
					default:
						return '';
				}
			},
			getAvailableActionsForAsset(asset) {
				let menu = [
					{
						title: 'Edit',
						icon: 'edit',
						callback: () => this.onEditClick(asset)
					},
					{
						title: 'Duplicate',
						icon: 'content_copy',
						callback: () => this.onDuplicateClick(asset.id)
					},
					{
						title: 'Retire',
						icon: 'mdi-cancel',
						callback: () => this.onRetireClick(asset.id)
					},
					{
						title: 'Un-retire',
						icon: 'mdi-backup-restore',
						callback: () => this.onActivateClick(asset.id)
					},
					{
						title: 'Delete',
						icon: 'delete',
						callback: () => this.onDeleteClick(asset.id)
					}
				];

				if (asset.assignedTo) {
					menu = menu.filter(x => x.title !== 'Delete');
				}

				if (asset.assignedTo || asset.status === 'Retired') menu = menu.filter(x => x.title !== 'Retire');

				if (asset.status !== 'Retired') {
					menu = menu.filter(x => x.title !== 'Un-retire');
				}

				return menu;
			},
			handleUpdateSearch(val) {
				this.search = val;
			},
			handleUpdateCategories(val) {
				this.filterCategories = val;
			},
			handleUpdateLocations(val) {
				this.filterLocations = val;
			},
			handleUpdateStasuses(val) {
				this.filterStatuses = val;
			},
			async handleAssignSave(payload) {
				const result = await this.assignAsset({ ...payload, assetIds: [this.assigningAsset.id] });
				if (result.isSuccess) {
					toast.success('Asset assigned successfully.');
					this.showAssignAssetDialog = false;
				}
			},
			async handleUnassign(note) {
				const result = await this.unassignAsset({ assetIds: [this.unassigningAsset.id], notes: note });
				if (result.isSuccess) {
					this.showUnassignDialog = false;
				}
			},
			onAddClick() {
				this.editingAsset = undefined;
				this.showAssetEditorDialog = true;
			},
			onEditClick(asset) {
				this.editingAsset = asset;
				this.showAssetEditorDialog = true;
			},
			assetIsOverdueService(asset) {
				if (!asset.nextServiceDate) return false;

				return moment(asset.nextServiceDate) < moment();
			},
			toggleSelection(item) {
				const index = this.selectedModel.findIndex(selectedItem => selectedItem.id === item.id);
				if (index >= 0) {
					this.selectedModel.splice(index, 1);
				} else {
					this.selectedModel.push(item);
				}
			}
		},
		created() {
			if (!this.hasLoadedAssets) {
				this.$store.dispatch('assets/get');
			}

			if (!this.hasLoadedAssetConfiguration) {
				this.$store.dispatch('assetConfiguration/get');
			}
		}
	};
</script>
